import Row from "@amzn/meridian/row";
import SideMenu, {
  SideMenuLink,
  SideMenuSpacer,
} from "@amzn/meridian/side-menu";
import Text from "@amzn/meridian/text";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  setAllFavoriteMessages,
  setConversationId,
  setInputText,
  setIsInputDisabled,
  setIsLoading,
  setResponses,
  setShowResult,
  clearStreamingHistory,
} from "src/components/pages/home/store/chatSlice";
import {
  ALIAS,
  CONVERSATION_ID,
  FAVORITE_QUESTION_CLICK,
  HISTORY_QUESTION_CLICK,
  NEW_CHAT_CLICK,
  TOAST_TIMEOUT,
} from "src/constants/constants";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { sendCustomRUMEvent } from "src/utils/awsRUM";
import { v4 as uuidv4 } from "uuid";
import "./sideMenu.scss";
import { Button, Icon } from "@amzn/meridian";
import { createToast } from "src/store/toastsSlice";
import deleteTokens from "@amzn/meridian-tokens/base/icon/delete";
import clipboardTokens from "@amzn/meridian-tokens/base/icon/clipboard";
import saveTokens from "@amzn/meridian-tokens/base/icon/save";
import documentTokens from "@amzn/meridian-tokens/base/icon/document";
import editTokens from "@amzn/meridian-tokens/base/icon/edit";
import cogTokens from "@amzn/meridian-tokens/base/icon/cog";
import supportHeadphonesTokens from "@amzn/meridian-tokens/base/icon/support-headphones";
import ChatService from "src/components/pages/home/service/chatService";
import { setSideMenu, setSideMenuSelectedHash } from "src/store/uiSlice";
const ChatSideMenu = () => {
  const { isSideMenuOpen, sideMenuSelectedHash } = useAppSelector(
    (state) => state.ui,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const homeRoute =
    location.pathname === "/saige"
      ? "/saige"
      : location.pathname === "/mcp"
        ? "/mcp"
        : "/";
  const dispatch = useAppDispatch();
  const {
    recentChats,
    isRecentChatsLoaded,
    isFavoriteMessagesLoaded,
    favoriteMessages,
  } = useAppSelector((state) => state.chatSlice);
  const { alias, firstName, isAuthorized, isAdministrator } = useAppSelector(
    (state) => state.user,
  );

  const getInitialHash = () => {
    if (location.pathname === "/change-region") return "#settings";
    if (location.pathname === "/admin") return "#adminReport";
    if (location.pathname === "/help-and-support") return "#helpAndSupport";
    return "#newChat";
  };

  const getInitialOpenLink = () => null;

  const [openLinkHash, setOpenLinkHash] = useState(getInitialOpenLink);
  const [hash, setHash] = useState(getInitialHash);

  // Update hash when triggered from another page (e.g., changeRegion cancel)
  useEffect(() => {
    if (sideMenuSelectedHash) {
      setHash(sideMenuSelectedHash);
      dispatch(setSideMenuSelectedHash(null));
    }
  }, [sideMenuSelectedHash, dispatch]);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const handleDeleteConfirmation = async (messageId: string) => {
    try {
      const { data } = await ChatService.removeFavorite(messageId);

      if (data.successful) {
        const favoriteMessages = await ChatService.getAllFavorites();
        dispatch(setAllFavoriteMessages(favoriteMessages));
      }
    } catch (error) {
      {
        dispatch(
          createToast({
            type: "error",
            message: "Failed to delete favorite",
            timeout: TOAST_TIMEOUT,
          }),
        );
      }
    }
  };

  const handleAdminReport = (hash: string) => {
    setHash(hash);
    navigate("/admin");
  };

  const initializeChatContext = (user_text) => {
    navigate(homeRoute); // Navigate to current home context
    dispatch(setShowResult(false));
    dispatch(setResponses(null));
    dispatch(clearStreamingHistory());
    dispatch(setIsInputDisabled(false));
    dispatch(setIsLoading(false));
    dispatch(setInputText(user_text));
    dispatch(setConversationId(uuidv4()));
  };

  const newChat = () => {
    initializeChatContext("");
    sendCustomRUMEvent(NEW_CHAT_CLICK, {
      [ALIAS]: alias,
    });
  };

  const clickHistoryQuestion = (user_text) => {
    initializeChatContext(user_text); // Always navigate to home for recent chats
    sendCustomRUMEvent(HISTORY_QUESTION_CLICK, {
      [ALIAS]: alias,
      [CONVERSATION_ID]: setConversationId,
    });
  };

  const clickFavoriteQuestion = (user_text) => {
    initializeChatContext(user_text);
    sendCustomRUMEvent(FAVORITE_QUESTION_CLICK, {
      [ALIAS]: alias,
      [CONVERSATION_ID]: setConversationId,
    });
  };

  return (
    <Row
      alignmentVertical="stretch"
      spacing="none"
      className="chat-side-menu-wrapper"
    >
      <SideMenu open={isSideMenuOpen} type="skinny" id="chat-side-menu">
        {isAdministrator && (
          <SideMenuLink
            href="#adminReport"
            selected={hash === "#adminReport"}
            onClick={(hash) => {
              handleAdminReport(hash);
              if (!isSideMenuOpen) {
                dispatch(setSideMenu(true));
              }
            }}
            icon={documentTokens}
          >
            Admin Report
          </SideMenuLink>
        )}
        <SideMenuLink
          href="#newChat"
          selected={hash === "#newChat"}
          onClick={(hash) => {
            setHash(hash);
            newChat();
            if (!isSideMenuOpen) {
              dispatch(setSideMenu(true));
            }
          }}
          icon={editTokens}
        >
          New Chat
        </SideMenuLink>
        {isAuthorized && (
          <SideMenuLink
            href="#favoritequestions"
            selected={hash === "#favoritequestions"}
            open={openLinkHash === "#favoritequestions"}
            onClick={(h) => {
              setHash(h);
              navigate(homeRoute);
              if (!isSideMenuOpen) {
                dispatch(setSideMenu(true));
                setOpenLinkHash(h);
              } else {
                setOpenLinkHash(
                  openLinkHash === "#favoritequestions" ? null : h,
                );
              }
            }}
            disabled={!favoriteMessages || favoriteMessages.length === 0}
            icon={saveTokens}
          >
            <span className="side-menu-favorite-title">
              Saved Queries
              {/* <Icon className="side-menu-star-icon" tokens={starFilledTokens} /> */}
            </span>
            {isSideMenuOpen &&
              (isFavoriteMessagesLoaded ? (
                favoriteMessages?.map((favorite) => (
                  <SideMenuLink
                    key={favorite.messageId}
                    href={`#fav-${favorite.messageId}`}
                    selected={hash === `#fav-${favorite.messageId}`}
                    onClick={(h) => {
                      setHash(h);
                      clickFavoriteQuestion(favorite.userText);
                    }}
                    onEscape={() => {
                      setHash("#favoritequestions");
                      setOpenLinkHash(null);
                    }}
                  >
                    <div className="favorite-question-container">
                      <div className="favorite-question-text">
                        <Text
                          type="b200"
                          id="side-menu-favorite-questions-truncate"
                        >
                          {favorite.userText}
                        </Text>
                        <Text
                          type="b100"
                          id="side-menu-favorite-chats-timestamp"
                        >
                          {formatTimestamp(+favorite.favoritedAt)}
                        </Text>
                      </div>
                      <Button
                        type="icon"
                        size="small"
                        id="delete-favorite-button"
                        onClick={() =>
                          handleDeleteConfirmation(favorite.messageId)
                        }
                      >
                        <Icon tokens={deleteTokens} />
                      </Button>
                    </div>
                  </SideMenuLink>
                ))
              ) : (
                <Text type="b100">Loading favorite messages...</Text>
              ))}
          </SideMenuLink>
        )}

        {isAuthorized && (
          <SideMenuLink
            href="#recentChats"
            selected={hash === "#recentChats"}
            open={openLinkHash === "#recentChats"}
            onClick={(h) => {
              setHash(h);
              navigate(homeRoute);
              if (!isSideMenuOpen) {
                dispatch(setSideMenu(true));
                setOpenLinkHash(h);
              } else {
                setOpenLinkHash(openLinkHash === "#recentChats" ? null : h);
              }
            }}
            disabled={recentChats.length === 0}
            icon={clipboardTokens}
          >
            Chat History
            {isSideMenuOpen &&
              (isRecentChatsLoaded ? (
                recentChats.map((chat) => (
                  <SideMenuLink
                    key={chat.messageId}
                    href={`#${chat.messageId}`}
                    selected={hash === `#${chat.messageId}`}
                    onClick={(h) => {
                      setHash(h);
                      clickHistoryQuestion(chat.userText);
                    }}
                    onEscape={() => {
                      setHash("#recentChats");
                      setOpenLinkHash(null);
                    }}
                  >
                    <Text type="b200" id="side-menu-recent-chats-truncate">
                      {chat.userText}
                    </Text>
                    <Text type="b100" id="side-menu-recent-chats-timestamp">
                      {formatTimestamp(+chat.timestamp)}
                    </Text>
                  </SideMenuLink>
                ))
              ) : (
                <Text type="b100">Loading recent chats...</Text>
              ))}
          </SideMenuLink>
        )}

        {/*
          Growing spacer pushes the pinned links to the bottom; the links stay
          real SideMenuLinks so they render as icons in skinny/collapsed mode
          (Meridian's footer slot can't render while collapsed). The scss keeps
          them stuck to the bottom via sticky while the chat list scrolls.
        */}
        <SideMenuSpacer />
        <SideMenuLink
          href="#helpAndSupport"
          selected={hash === "#helpAndSupport"}
          onClick={(h) => {
            setHash(h);
            navigate("/help-and-support");
            if (!isSideMenuOpen) {
              dispatch(setSideMenu(true));
            }
          }}
          icon={supportHeadphonesTokens}
        >
          Help and Support
        </SideMenuLink>
        <SideMenuLink
          href="#settings"
          selected={hash === "#settings"}
          onClick={(h) => {
            setHash(h);
            navigate("/change-region");
            if (!isSideMenuOpen) {
              dispatch(setSideMenu(true));
            }
          }}
          icon={cogTokens}
        >
          Settings
        </SideMenuLink>
      </SideMenu>
    </Row>
  );
};

export default ChatSideMenu;
